<template>
  <VAutocomplete
    v-model="selectedId"
    :items="[...autocompleteItems]"
    :rules="[textIsNotEmpty]"
    :placeholder="t('Search...')"
    :loading="casesLoading"
    :disabled="casesLoading"
    class="mb-1 text-[#17485D] font-titillium tracking-[0.15px] leading-[1.5]"
  >
    <template v-slot:append-item>
      <div v-intersect="onIntersect" class="text-center">
        <VProgressCircular v-if="nextCasesLoading" indeterminate />
      </div>
    </template>
  </VAutocomplete>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { textIsNotEmpty } from '@/util/validation/validationRules'
import { storeToRefs } from 'pinia'
import { useCaseStore } from '@/stores/case'
import { computed, onMounted } from 'vue'
import { type CaseDTO, UrgencyDTO } from '@/generated'
import { parseISO } from 'date-fns'
import type { SelectOption } from '@/types/SelectOption'
import { useFormatDate } from '@/composables/useFormatDate'

const { urgencyType = UrgencyDTO.ROUTINE } = defineProps<{
  urgencyType?: UrgencyDTO
}>()

const caseStore = useCaseStore()

const selectedId = defineModel<string>()
const { t } = useI18n()
const { caseList, casesLoading, nextCasesLoading } = storeToRefs(caseStore)
const { formatDateOnly } = useFormatDate()

onMounted(caseStore.refreshCases)

const autocompleteItems = computed<SelectOption<string>[]>(() =>
  caseList.value.map((caze: CaseDTO) => {
    const isRoutingDisabled =
      (urgencyType === UrgencyDTO.EMERGENCY && caze.emergencyRoutingUnavailable) ||
      (urgencyType === UrgencyDTO.URGENT && caze.urgentRoutingUnavailable) ||
      (urgencyType === UrgencyDTO.ROUTINE && caze.routineRoutingUnavailable)

    const isConsultationsDisabled =
      urgencyType !== UrgencyDTO.EMERGENCY && !caze.consultationsEnabled

    return {
      value: caze.id,
      title: caze.patient.dateOfBirth
        ? `${caze.externalId}, ${caze.patient.lastName}, ${caze.patient.firstName}, ${formatDateOnly(parseISO(caze.patient.dateOfBirth))}`
        : `${caze.externalId}, ${caze.patient.lastName}, ${caze.patient.firstName}`,
      props: {
        disabled: isRoutingDisabled || isConsultationsDisabled
      }
    }
  })
)

const onIntersect = (isIntersecting: boolean) => {
  if (isIntersecting) {
    caseStore.loadMoreCases()
  }
}
</script>
